export const MAX_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;
export const DEFAULT_DASHBOARD_RANGE = { from: "now-3d", to: "now" };
export const RETENTION_HELP =
  "Raw telemetry is retained for 30 days. Choose dates within the last 30 days, up to now.";

/** Resolve date math in Africa/Lagos, matching the Grafana/API dashboard zone. */
export function dashboardTime(value: string, end: boolean, now = Date.now()): number {
  if (/^\d{10}$|^\d{13}$/.test(value)) return Number(value) * (value.length === 10 ? 1000 : 1);
  const relative = /^now(?:-([1-9]\d{0,2})([smhdwM]))?(?:\/([dhwM]))?(?:\+([1-9]\d?)d)?$/.exec(
    value,
  );
  if (!relative) {
    // A timezone is mandatory: browser-local interpretation must not shift deep links.
    return /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? Date.parse(value) : NaN;
  }
  const [, amount, unit, rounding, addition] = relative;
  const local = new Date(now + 60 * 60 * 1000);
  if (amount) {
    if (unit === "M") {
      const day = local.getUTCDate();
      local.setUTCDate(1);
      local.setUTCMonth(local.getUTCMonth() - Number(amount));
      const lastDay = new Date(
        Date.UTC(local.getUTCFullYear(), local.getUTCMonth() + 1, 0),
      ).getUTCDate();
      local.setUTCDate(Math.min(day, lastDay));
    } else {
      const units: Record<string, number> = {
        s: 1000,
        m: 60000,
        h: 3600000,
        d: 86400000,
        w: 604800000,
      };
      local.setTime(local.getTime() - Number(amount) * (units[unit ?? "s"] ?? 0));
    }
  }
  if (rounding) {
    local.setUTCMinutes(0, 0, 0);
    if (rounding !== "h") local.setUTCHours(0);
    if (rounding === "w") local.setUTCDate(local.getUTCDate() - ((local.getUTCDay() + 6) % 7));
    if (rounding === "M") local.setUTCDate(1);
    if (end) {
      if (rounding === "M") local.setUTCMonth(local.getUTCMonth() + 1);
      else
        local.setTime(local.getTime() + ({ h: 3600000, d: 86400000, w: 604800000 }[rounding] ?? 0));
      local.setTime(local.getTime() - 1);
    }
  }
  if (addition) local.setUTCDate(local.getUTCDate() + Number(addition));
  return local.getTime() - 60 * 60 * 1000;
}

export function dashboardRangeError(from: string, to: string, now = Date.now()): string | null {
  const start = dashboardTime(from, false, now);
  const end = dashboardTime(to, true, now);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end)
    return "Choose a start time before the end time.";
  if (start < now - MAX_LOOKBACK_MS || end < now - MAX_LOOKBACK_MS)
    return "Choose dates within the last 30 days.";
  if (start > now || end > now) return "Dates cannot be in the future.";
  return null;
}

export function normalizeDashboardRange(
  from: string,
  to: string,
  now = Date.now(),
): { from: string; to: string } {
  return dashboardRangeError(from, to, now) ? DEFAULT_DASHBOARD_RANGE : { from, to };
}

export function lagosInputValue(milliseconds: number): string {
  return new Date(milliseconds + 60 * 60 * 1000).toISOString().slice(0, 16);
}
